<template>
  <section class="container mx-auto py-6 md:pt-10 md:pb-15 w-[90%] md:w-full">
    <div
      class="pb-10 md:pb-8 filter flex flex-col md:flex-row justify-end gap-6"
    >
      <template v-if="applyFiltersLoading">
        <UtilsSkeletonLoading class="w-full md:w-[276px] h-10" />
        <UtilsSkeletonLoading class="w-full md:w-[276px] h-10" />
      </template>
      <template v-else>
        <v-select
          v-model="filterData.industry"
          label="name"
          :options="options.industries"
          :reduce="option => option.name"
          placeholder="Industry"
          class="text-bw-800 w-full md:w-[305px]"
          @update:model-value="onInputIndustry"
        >
        </v-select>
        <v-select
          v-model="filterData.expertise"
          label="name"
          :options="options.expertises"
          :reduce="option => option.name"
          placeholder="Expertise"
          class="text-bw-800 w-full md:w-[305px]"
          @update:model-value="onInputExpertise"
        >
        </v-select>
      </template>
    </div>

    <div
      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-9 gap-y-8 md:gap-y-15"
    >
      <template v-if="isLoading">
        <div v-for="i in 3" :key="i">
          <UtilsSkeletonLoading class="rounded-lg h-40 w-full mb-4 card-img" />
          <UtilsSkeletonLoading class="w-full h-14 mb-3" />
          <UtilsSkeletonLoading class="w-full h-12" />
        </div>
      </template>
      <div
        v-else-if="contentsData.length === 0"
        class="flex flex-col justify-center md:min-h-[35vh] col-span-3"
      >
        <h2
          class="text-[22px] leading-normal md:text-[40px] md:leading-[50px] font-medium text-bw-900 mb-5 mx-auto text-center"
        >
          We Looked Everywhere
        </h2>
        <p
          class="text-sm sm:text-base text-bw-800 w-[90%] sm:w-3/4 md:w-2/5 mx-auto text-center"
        >
          Sorry, the Case Study you are looking for cannot be found. Please try
          to enter another filters.
        </p>
      </div>
      <template v-else>
        <ArticleCard
          v-for="content in contentsData"
          :key="content.id"
          :with-logo="true"
          :logo="
            content.attributes?.study_case_client?.data?.attributes?.image?.data
              ?.attributes?.url
          "
          :title="content.attributes?.card_title"
          :desc="content.attributes?.layout_component?.[0]?.description"
          :image-url="content.attributes?.image?.data?.attributes?.url"
          :slug="`/work/${content.attributes?.slug}`"
        />
        <template v-if="isLoadingMore">
          <div v-for="i in 3" :key="i">
            <UtilsSkeletonLoading
              class="rounded-lg h-40 w-full mb-4 card-img"
            />
            <UtilsSkeletonLoading class="w-full h-14 mb-3" />
            <UtilsSkeletonLoading class="w-full h-12" />
          </div>
        </template>
      </template>
    </div>
    <div
      v-if="contentMeta?.pagination?.page < contentMeta?.pagination?.pageCount"
      class="mx-auto text-center mt-8 md:mt-10"
    >
      <CommonLoadMoreButton @click="loadMore" />
    </div>
  </section>
</template>

<script setup>
import { useGlobalStore } from '~/store/global'

const global = useGlobalStore()

const options = ref({
  industries: [],
  expertises: []
})

const route = useRoute()
const router = useRouter()

const isLoading = ref(true)
const isLoadingMore = ref(false)
const applyFiltersLoading = ref(true)
const page = ref(1)

const contentsData = ref([])
const contentMeta = ref({})

const filterData = ref({
  industry: route.query.industry || null,
  expertise: route.query.expertise || null
})

const caseQuery = ref({
  'pagination[page]': page,
  'pagination[pageSize]': 12,
  populate:
    'image,layout_component,layout_component.type,study_case_client.image,expertices',
  'filters[layout][$eq]': 'study-case',
  sort: 'publish_at:desc'
})

if (route.query.industry) {
  caseQuery.value['filters[$and][1][study_case_client][industry][title][$eq]'] =
    route.query.industry
}
if (route.query.expertise) {
  caseQuery.value['filters[$and][2][expertices][title][$eq]'] =
    route.query.expertise
}
if (route.query.org_type) {
  caseQuery.value['filters[study_case_client][client_types][type][$in][0]'] =
    route.query.org_type
}

const [caseS, industries, expertises] = await Promise.all([
  useSuitmediaApiData(`/api/contents`, {
    query: { ...caseQuery.value }
  }),
  useSuitmediaApiData(`/api/industries`, {
    query: {
      'pagination[pageSize]': 50,
      'fields[0]': 'id',
      'fields[1]': 'title',
      'sort[0]': 'title:asc'
    }
  }),
  useSuitmediaApiData(`/api/expertices`, {
    query: {
      'pagination[pageSize]': 50,
      'fields[0]': 'id',
      'fields[1]': 'title',
      'sort[0]': 'title:asc'
    }
  })
])

isLoading.value =
  caseS.pending.value || industries.pending.value || expertises.pending.value
applyFiltersLoading.value = caseS.pending.value

if (caseS.error.value) {
  throw createError({
    statusCode: 404,
    message: `Case Studies does not exist`
  })
}

contentsData.value = caseS.data.value.data
contentMeta.value = caseS.data.value.meta

options.value.expertises = expertises.data.value.data.map(item => {
  return {
    id: item.id,
    name: item.attributes.title
  }
})

options.value.industries = industries.data.value.data.map(item => {
  return {
    id: item.id,
    name: item.attributes.title
  }
})

const loadMore = async e => {
  e.currentTarget.blur()
  isLoadingMore.value = true
  page.value++
  await getContents(true)
  isLoadingMore.value = false
  global.changeLoadedMore()
}

const getContents = async (isLoadMore = false) => {
  const query = {
    'pagination[page]': page,
    'pagination[pageSize]': 12,
    populate:
      'image,layout_component,layout_component.type,study_case_client.image,expertices',
    'filters[layout][$eq]': 'study-case',
    sort: 'publish_at:desc'
  }

  if (route.query.org_type) {
    query['filters[study_case_client][client_types][type][$in][0]'] =
      route.query.org_type
  }

  if (filterData.value.industry) {
    query['filters[$and][1][study_case_client][industry][title][$eq]'] =
      filterData.value.industry
  }
  if (filterData.value.expertise) {
    query['filters[$and][2][expertices][title][$eq]'] =
      filterData.value.expertise
  }

  const { data, error, pending } = await useSuitmediaApiData(`/api/contents`, {
    query
  })

  isLoading.value = pending.value
  applyFiltersLoading.value = pending.value

  if (error.value) {
    throw createError({
      statusCode: 404,
      message: `Case Studies does not exist`
    })
  }

  if (isLoadMore) {
    contentsData.value = [...contentsData.value, ...data.value.data]
    contentMeta.value = data.value.meta
  } else {
    contentsData.value = data.value.data
    contentMeta.value = data.value.meta
  }
}

const onInputExpertise = async expertise => {
  isLoading.value = true
  page.value = 1

  filterData.value.industry = null
  const currentQuery = route.query
  delete currentQuery.expertise

  router.push({ query: { expertise } })
  await getContents()
}
const onInputIndustry = async val => {
  isLoading.value = true
  page.value = 1

  filterData.value.expertise = null
  const currentQuery = route.query
  delete currentQuery.industry

  router.push({ query: { industry: val } })
  await getContents()
}

// watch(
//   () => [route.query.industry, route.query.expertise],
//   () => {
//     getContents()
//   }
// )
</script>

<style lang="scss" scoped>
.filter {
  :deep(.vs--open) {
    .vs__dropdown-toggle {
      padding: 10px;

      background: linear-gradient(#ff6700, #ff6700),
        linear-gradient(#ff6700, #ff6700), linear-gradient(#ff6700, #ff6700);
      background-size:
        1px 20%,
        100% 1px,
        1px 20%;
      background-position:
        bottom left,
        bottom center,
        bottom right;
      background-repeat: no-repeat;

      border: none;
    }
  }
  :deep(.vs__dropdown-toggle) {
    padding: 10px;

    background: linear-gradient(#a5a5a5, #a5a5a5),
      linear-gradient(#a5a5a5, #a5a5a5), linear-gradient(#a5a5a5, #a5a5a5);
    background-size:
      1px 20%,
      100% 1px,
      1px 20%;
    background-position:
      bottom left,
      bottom center,
      bottom right;
    background-repeat: no-repeat;

    border: none;
    border-radius: 0;
    // color: #999;
  }
  :deep(.vs__selected-options) {
    flex-wrap: nowrap;
  }
}
</style>
